import Image from 'next/image'
import PropTypes from 'prop-types'
import React, { useCallback, useState, useEffect, useMemo } from 'react'
import { NEXT_IMAGE_QUALITY } from 'src/common/constants/imageQuality'
import { contentfulImageLoader } from './loader'
import { checkWebpSupport } from './image'

/**
 * Enhanced Image component with WebP support and error handling
 * @param {Object} props - Component props
 */
const Img = (props) => {
  const {
    className = '',
    imgClassName = '',
    alt,
    src = '',
    dataTestId = '',
    layout = 'fill',
    objectFit = 'contain',
    height = 1,
    width = 1,
    priority = false,
    loading = undefined,
    quality = NEXT_IMAGE_QUALITY,
    isBackgroundImage = false,
    styles,
    objectPosition,
    imgStyle,
    imgWidth = '100%',
    customLoader = true,
    isPngAvailable = false,
    unoptimized = false,
  } = props

  // Memoize contentful check to avoid recalculation
  const isContentful = useMemo(
    () => src?.includes('images.ctfassets.net'),
    [src]
  )

  // Use lazy initial state to avoid unnecessary computation during SSR
  const [supportsWebp, setSupportsWebp] = useState(() => true)
  const [newSrc, setNewSrc] = useState(() => src)
  const [isClient, setIsClient] = useState(false)

  // Run only on client-side
  useEffect(() => {
    setIsClient(true)
  }, [])

  const handleError = useCallback(
    async (e) => {
      if (!isClient) return // Skip during SSR
      if (
        isContentful &&
        supportsWebp &&
        e.target.currentSrc?.includes('webp')
      ) {
        setSupportsWebp(false)

        const fallbackSrc = src.replace('.webp', '.png')
        setNewSrc(isPngAvailable ? fallbackSrc : src)
      }
    },
    [isContentful, supportsWebp, src, isClient, isPngAvailable]
  )

  useEffect(() => {
    if (!isClient || priority) return // Skip WebP detection for LCP images

    let mounted = true

    const initImage = async () => {
      try {
        const isSupported = await checkWebpSupport()
        if (!mounted) return

        if (!isSupported) {
          const fallbackSrc = src.replace('.webp', '.png')
          if (!mounted) return
          setSupportsWebp(false)
          setNewSrc(isPngAvailable ? fallbackSrc : src)
        } else {
          setSupportsWebp(true)
          setNewSrc(src)
        }
      } catch (error) {
        console.error('Image initialization error:', error)
        if (mounted) {
          setNewSrc(src) // Fallback to original source
        }
      }
    }

    initImage()

    return () => {
      mounted = false
    }
  }, [src, isClient, isPngAvailable, priority])

  // Memoize image props to prevent unnecessary recalculations
  const imgProps = useMemo(
    () => ({
      src: newSrc,
      style: { objectFit, objectPosition, ...imgStyle },
      className: imgClassName,
      preload: priority,
      ...(priority && { fetchPriority: 'high' }),
      quality,
      unoptimized,
      // `loading` lets hidden-but-in-DOM images (closed drawer, scroll-reveal
      // bars) stay eager without claiming high fetch priority / a preload slot
      loading: priority ? 'eager' : loading ?? 'lazy',
      sizes: layout === 'fill' ? '(max-width: 640px) 100vw, 50vw' : undefined,
      ...(isContentful &&
        customLoader && {
          loader: contentfulImageLoader(supportsWebp),
        }),
      ...(layout !== 'fill' && { height, width }),
      onError: handleError,
    }),
    [
      newSrc,
      objectFit,
      objectPosition,
      imgStyle,
      imgClassName,
      priority,
      loading,
      quality,
      unoptimized,
      layout,
      isContentful,
      customLoader,
      supportsWebp,
      height,
      width,
      handleError,
    ]
  )

  // Memoize container className
  const containerClassName = useMemo(
    () =>
      `${isBackgroundImage ? 'absolute -z-1' : 'relative'} ${
        layout === 'fill' ? `${className} overflow-hidden` : className
      }`,
    [isBackgroundImage, layout, className]
  )

  if (!src) return null

  // Apply responsive and cover styles
  if (layout === 'responsive') {
    imgProps.className = `${imgProps.className} min-h-full max-h-full min-w-full max-w-full aspect-square object-contain`
    imgProps.style = {
      ...imgProps.style,
      height: 'auto',
      width: imgWidth,
    }
    imgProps.sizes = '100vw'
  }

  if (objectFit === 'cover') {
    imgProps.className = `${imgProps.className} object-cover h-full`
  }

  return (
    <div className={containerClassName} data-testid={dataTestId} style={styles}>
      {layout === 'fill' ? (
        <Image alt={alt} fill {...imgProps} />
      ) : (
        <Image alt={alt} {...imgProps} />
      )}
    </div>
  )
}

Img.propTypes = {
  objectPosition: PropTypes.string,
  imgStyle: PropTypes.object,
  layout: PropTypes.string,
  objectFit: PropTypes.string,
  className: PropTypes.string,
  imgClassName: PropTypes.string,
  dataTestId: PropTypes.string,
  src: PropTypes.string.isRequired,
  alt: PropTypes.string.isRequired,
  height: PropTypes.number,
  width: PropTypes.number,
  isBackgroundImage: PropTypes.bool,
  priority: PropTypes.bool,
  loading: PropTypes.oneOf(['eager', 'lazy']),
  quality: PropTypes.number,
  styles: PropTypes.object,
  imgWidth: PropTypes.string,
  customLoader: PropTypes.bool,
  isPngAvailable: PropTypes.bool,
  unoptimized: PropTypes.bool,
}

export default React.memo(Img)
